import { useMemo } from 'react'
import { useRateConvert } from '@web/walter-lib/stream'
import { orderBy } from 'lodash'
import Big from '@web/walter-lib/big.js'
import { SearchCollectionData } from '../types'
import { makeFilterByCodeName } from '~utils/filters'
import { useBaseCurrency } from '~hooks/useBaseCurrency'
import { useData as useCoinsData } from '~pages/MarketPage/AllCoins/useData'
import { useData as useCoinsInfoData } from '~components/CoinsTable/hooks/useData'
import { useCommonCMSConfig } from '~api'
import { useBalancesPerCurrency } from '~hooks/useBalancesPerCurrency'

export const useData = (searchString: string) => {
    const { isNewRateService } = useCommonCMSConfig()?.data?.features ?? {}
    const { data: coinsData, isLoading: isCoinsDataLoading } = useCoinsData()
    const { calculatedBaseCurrency: baseCurrency } = useBaseCurrency()
    const { data } = useCoinsInfoData(coinsData, undefined)
    const rateConvert = useRateConvert()
    const { balances } = useBalancesPerCurrency()

    const collectionData = useMemo(() => {
        const searchCollectionData: SearchCollectionData[] = []

        data?.forEach(item => {
            const balance = balances?.[item.code]?.available ?? '0'
            let baseBalance

            if (isNewRateService) {
                baseBalance = new Big(balance).times(item.price ?? 0).toNumber()
            } else {
                baseBalance = rateConvert?.convertApprox(item.code, baseCurrency, balance) ?? 0
            }

            searchCollectionData.push({ ...item, baseBalance, balance })
        })

        return searchCollectionData
    }, [balances, baseCurrency, data, rateConvert, isNewRateService])

    const searchData = useMemo(
        () => collectionData.filter(makeFilterByCodeName(searchString)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [searchString]
    )

    const trendingData = collectionData.some(({ volume, baseBalance, code }) =>
        !volume || volume === '0' ? baseBalance || code : volume
    )
        ? orderBy(
              collectionData,
              ({ volume, baseBalance, code }) => (!volume || volume === '0' ? baseBalance || code : volume),
              'desc'
          ).slice(0, 5)
        : []

    const isLoading = isCoinsDataLoading || !trendingData.length

    return { searchData, trendingData, collectionData, isLoading }
}
